import type { CSSProperties } from "react";

import { LogoOI } from "@/components/common/LogoOI";

/**
 * Il segno che apre una riga: il marchio dell'Osservatorio al posto del
 * glifo.
 *
 * Fino al 13/09/2026 ogni occhiello del sito cominciava con un carattere
 * del vocabolario grafico — ✦ ◆ ◈ ▤ ⬡ ◉ ◇ — scelto a seconda della
 * sezione. Erano una ventina di segni diversi per dire la stessa cosa,
 * "qui comincia una fascia", e nessuno di loro era il marchio. Adesso è
 * il marchio, uno solo, in tutto il sito: il segno «OI», cioè il logo
 * senza la scritta (<LogoOI variant="segno">, lo stesso
 * dell'intestazione). Il glifo resta dove non apre niente: i bottoni, i
 * contrassegni (§ riservato), le targhe delle entità, i menu, le voci di
 * navigazione.
 *
 * ## Perché è un componente e non una riga copiata
 *
 * Le righe d'occhiello sono una novantina, sparse su venticinque file, e
 * ognuna ha un contorno suo (filetto che pulsa, rimando a destra, corpo
 * e spaziatura diversi). Quello che hanno in comune è solo il segno:
 * qui sta la sua misura, il suo respiro e il modo di allinearlo, e
 * cambiare quelle tre cose vuol dire toccare un file solo.
 *
 * ## Le misure
 *
 * `h-[1.6em]` e non un numero di pixel: gli occhielli vanno da 10 a 13
 * pixel di corpo, e il segno deve crescere con loro. A 10 fa 16 pixel, a
 * 13 ne fa 21, cioè sempre poco più di una maiuscola e mezza.
 * La larghezza è `auto` perché il segno è 445x355, cioè più largo che
 * alto: fissargli tutti e due i lati lo schiaccia.
 *
 * `self-center` perché quelle righe allineano alle basi (`items-baseline`)
 * e un disegno non ha una linea di base: appoggiato come il testo
 * resterebbe appeso più in basso della riga.
 *
 * ## Il colore
 *
 * Nessuno: il tracciato è in `currentColor` e prende quello della riga,
 * come faceva il glifo. Chi vuole il tono dell'accento passa
 * `className="text-accent"`, chi ha un tono suo passa `style`.
 */
export function SegnoFascia({
  className,
  /** Il respiro lento che avevano i glifi degli occhielli
   *  (`oi-breathe`). Non è acceso di suo: su una pagina con dieci
   *  occhielli dieci marchi che pulsano insieme sono un lampeggio. Si
   *  accende dov'era acceso prima. */
  animato = false,
  style,
}: {
  className?: string;
  animato?: boolean;
  style?: CSSProperties;
}) {
  return (
    <span
      aria-hidden
      className={`inline-flex shrink-0 self-center${
        animato ? " animate-[oi-breathe_5.4s_ease-in-out_infinite]" : ""
      }${className ? ` ${className}` : ""}`}
      style={style}
    >
      <LogoOI variant="segno" decorative className="h-[1.6em] w-auto" />
    </span>
  );
}
